/**
 * Team feature constants — RBAC levels, domains, fixture data, and static config.
 *
 * Consolidated from data/rbac.ts, data/roles.ts, data/resources.ts, data/members.ts,
 * data/teams.ts, data/member-views.ts, data/join-requests.ts, data/invites.ts.
 * Types are defined in `../api/schemas.ts`.
 */
import type { ComponentType } from 'react';
import {
  IconBot,
  IconClipboard,
  IconCode,
  IconEye,
  IconScan,
  IconSearch,
  IconServer,
  IconShieldUser,
  IconUsers,
  type IconProps,
} from '@shared/icons';
import type {
  Domain,
  LastSignInBucket,
  Level,
  LevelMeta,
  MemberFilters,
  PermissionRow,
  PermissionState,
  RbacRole,
  RoleDef,
  ScopeResource,
  ScopeResourceType,
  TeamRole,
} from '../api/schemas';

type IconComponent = ComponentType<IconProps>;

/* ─────────────────────────────── Levels ───────────────────────────────── */

export const LEVELS: ReadonlyArray<Level> = ['none', 'view', 'full'];

export const LEVEL_META: Readonly<Record<Level, LevelMeta>> = {
  none: {
    label: 'No access',
    short: 'None',
    fgClass: 'text-aq-ink-muted',
    bgClass: 'bg-aq-zebra',
    borderClass: 'border-aq-border',
    rank: 0,
  },
  view: {
    label: 'View only',
    short: 'View',
    fgClass: 'text-aq-info',
    bgClass: 'bg-aq-info-soft',
    borderClass: 'border-aq-border',
    rank: 1,
  },
  full: {
    label: 'Full access',
    short: 'Full',
    fgClass: 'text-aq-good',
    bgClass: 'bg-aq-good-soft',
    borderClass: 'border-aq-good',
    rank: 2,
  },
};

/* ─────────────────────────────── Domains ──────────────────────────────── */

export const DOMAINS: ReadonlyArray<Domain> = [
  {
    key: 'userManagement',
    label: 'User management',
    icon: IconUsers,
    blurb: 'Invite, remove and assign roles.',
  },
  {
    key: 'vulnerabilityScanning',
    label: 'Vulnerability scanning',
    icon: IconScan,
    blurb: 'Run and read scan results.',
  },
  {
    key: 'mcpServerManagement',
    label: 'MCP servers',
    icon: IconServer,
    blurb: 'Register and configure MCP servers.',
  },
  {
    key: 'agentServerManagement',
    label: 'Agent servers',
    icon: IconBot,
    blurb: 'Approve and govern AI agents.',
  },
  {
    key: 'auditLog',
    label: 'Audit log',
    icon: IconClipboard,
    blurb: 'Read the immutable change record.',
  },
];

/* ──────────────────────────────── RBAC Roles (matrix-based) ──────────── */

export const RBAC_ROLES: ReadonlyArray<RbacRole> = [
  {
    key: 'org_admin',
    name: 'Organization Admin',
    icon: IconShieldUser as IconComponent,
    system: true,
    desc: 'Full control of the organization, its people and every resource.',
    matrix: {
      userManagement: 'full',
      vulnerabilityScanning: 'full',
      mcpServerManagement: 'full',
      agentServerManagement: 'full',
      auditLog: 'full',
    },
  },
  {
    key: 'iam_admin',
    name: 'IAM Admin',
    icon: IconShieldUser as IconComponent,
    system: true,
    desc: 'Owns identity and access. Manages people and reads everything else.',
    matrix: {
      userManagement: 'full',
      vulnerabilityScanning: 'view',
      mcpServerManagement: 'view',
      agentServerManagement: 'view',
      auditLog: 'full',
    },
  },
  {
    key: 'developer',
    name: 'Developer',
    icon: IconCode as IconComponent,
    system: true,
    desc: 'Builds and operates servers and agents. Read-only on people.',
    matrix: {
      userManagement: 'view',
      vulnerabilityScanning: 'full',
      mcpServerManagement: 'full',
      agentServerManagement: 'full',
      auditLog: 'view',
    },
  },
  {
    key: 'analyst',
    name: 'Security Analyst',
    icon: IconSearch as IconComponent,
    system: true,
    desc: 'Investigates risk. Runs scans, observes servers and agents.',
    matrix: {
      userManagement: 'none',
      vulnerabilityScanning: 'full',
      mcpServerManagement: 'view',
      agentServerManagement: 'view',
      auditLog: 'view',
    },
  },
  {
    key: 'viewer',
    name: 'Viewer',
    icon: IconEye as IconComponent,
    system: true,
    desc: 'Read-only visibility across the whole organization.',
    matrix: {
      userManagement: 'view',
      vulnerabilityScanning: 'view',
      mcpServerManagement: 'view',
      agentServerManagement: 'view',
      auditLog: 'view',
    },
  },
  {
    key: 'auditor',
    name: 'Auditor',
    icon: IconClipboard as IconComponent,
    system: false,
    desc: 'Custom role. Owns the audit trail, observes everything else.',
    matrix: {
      userManagement: 'view',
      vulnerabilityScanning: 'view',
      mcpServerManagement: 'view',
      agentServerManagement: 'view',
      auditLog: 'full',
    },
  },
];

/* ─────────────────────────────── Permission rows (role-def based) ───── */

export const PERMISSION_ROWS: readonly PermissionRow[] = [
  {
    key: 'view-inventory',
    label: 'View inventory',
    description: 'See agents, MCP servers, tools.',
  },
  { key: 'edit-agents', label: 'Edit agents', description: 'Register, verify, retire agents.' },
  {
    key: 'add-mcp',
    label: 'Add MCP servers',
    description: 'Register and configure new MCP endpoints.',
  },
  {
    key: 'manage-policies',
    label: 'Manage policies',
    description: 'Author and publish policy rules.',
  },
  {
    key: 'approve-intent',
    label: 'Approve intent plans',
    description: 'Sign off on pending intent plan approvals.',
  },
  {
    key: 'view-audit',
    label: 'View audit log',
    description: 'Read-only access to the full audit trail.',
  },
  {
    key: 'manage-keys',
    label: 'Manage API keys',
    description: 'Create, rotate, and revoke service keys.',
  },
  {
    key: 'invite-members',
    label: 'Invite members',
    description: 'Send invites and resend pending ones.',
  },
  {
    key: 'assign-roles',
    label: 'Assign roles',
    description: 'Promote, demote, or remove members.',
  },
  {
    key: 'billing',
    label: 'Billing & plans',
    description: 'Manage subscriptions and seat counts.',
  },
];

export const ROLE_DEFS: readonly RoleDef[] = [
  {
    key: 'org_admin',
    label: 'Org Admin',
    description: 'Operate the platform end-to-end. Full access to all resources.',
    members: 0,
    permissions: {
      'view-inventory': true,
      'edit-agents': true,
      'add-mcp': true,
      'manage-policies': true,
      'approve-intent': true,
      'view-audit': true,
      'manage-keys': true,
      'invite-members': true,
      'assign-roles': true,
      billing: true,
    },
  },
  {
    key: 'iam_admin',
    label: 'IAM Admin',
    description: 'Owns identity, roles, and API key lifecycle.',
    members: 0,
    permissions: {
      'view-inventory': true,
      'edit-agents': false,
      'add-mcp': false,
      'manage-policies': false,
      'approve-intent': false,
      'view-audit': true,
      'manage-keys': true,
      'invite-members': true,
      'assign-roles': true,
      billing: false,
    },
  },
  {
    key: 'developer',
    label: 'Developer',
    description: 'Builds and registers agents and MCP servers.',
    members: 0,
    permissions: {
      'view-inventory': true,
      'edit-agents': true,
      'add-mcp': true,
      'manage-policies': false,
      'approve-intent': false,
      'view-audit': true,
      'manage-keys': false,
      'invite-members': false,
      'assign-roles': false,
      billing: false,
    },
  },
  {
    key: 'analyst',
    label: 'Analyst',
    description: 'Approves intent plans and reviews policy changes.',
    members: 0,
    permissions: {
      'view-inventory': true,
      'edit-agents': false,
      'add-mcp': false,
      'manage-policies': true,
      'approve-intent': true,
      'view-audit': true,
      'manage-keys': false,
      'invite-members': false,
      'assign-roles': false,
      billing: false,
    },
  },
  {
    key: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to inventory and audit log.',
    members: 0,
    permissions: {
      'view-inventory': true,
      'edit-agents': false,
      'add-mcp': false,
      'manage-policies': false,
      'approve-intent': false,
      'view-audit': true,
      'manage-keys': false,
      'invite-members': false,
      'assign-roles': false,
      billing: false,
    },
  },
];

/** Human-readable label for a role. Used in row badges across the slice. */
export const ROLE_LABEL: Readonly<Record<TeamRole, string>> = {
  org_admin: 'Org Admin',
  iam_admin: 'IAM Admin',
  developer: 'Developer',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

/** Convert a FE display role to the BE enum value for API calls. */
export function toBackendRole(role: string): string {
  return role;
}

/** Convert a BE role string to the FE display role. */
export function fromBackendRole(role: string): string {
  return role;
}

/* ─────────────────────────────── Resources ─────────────────────────────── */

export const SCOPE_RESOURCES: Readonly<Record<ScopeResourceType, ReadonlyArray<ScopeResource>>> = {
  mcps: [
    { type: 'mcps', id: 'stripe', label: 'Stripe MCP', sublabel: '14 tools', iconSlug: 'stripe' },
    { type: 'mcps', id: 'github', label: 'GitHub MCP', sublabel: '11 tools', iconSlug: 'github' },
    { type: 'mcps', id: 'slack', label: 'Slack MCP', sublabel: '7 tools', iconSlug: 'slack' },
    { type: 'mcps', id: 'notion', label: 'Notion MCP', sublabel: '6 tools', iconSlug: 'notion' },
    { type: 'mcps', id: 'jira', label: 'Jira MCP', sublabel: '9 tools', iconSlug: 'jira' },
    { type: 'mcps', id: 'openai', label: 'OpenAI MCP', sublabel: '5 tools', iconSlug: 'openai' },
    { type: 'mcps', id: 'ledger-mcp', label: 'ledger-mcp', sublabel: '8 tools' },
    { type: 'mcps', id: 'research-mcp', label: 'research-mcp', sublabel: '6 tools' },
  ],
  agents: [
    { type: 'agents', id: 'claims-triage-bot', label: 'claims-triage-bot', sublabel: 'prod' },
    { type: 'agents', id: 'finops-analyzer', label: 'finops-analyzer', sublabel: 'prod' },
    { type: 'agents', id: 'pr-summarizer', label: 'pr-summarizer', sublabel: 'staging' },
    { type: 'agents', id: 'payments-rec', label: 'payments-rec', sublabel: 'prod' },
    { type: 'agents', id: 'billing-prod', label: 'billing-prod', sublabel: 'prod' },
    { type: 'agents', id: 'helm-deployer', label: 'helm-deployer', sublabel: 'staging' },
  ],
  policies: [
    {
      type: 'policies',
      id: 'pol_claims_triage_safe',
      label: 'Claims Triage · Safe Mode',
      sublabel: 'active',
    },
    {
      type: 'policies',
      id: 'pol_stripe_refunds',
      label: 'Stripe · Refund Approval',
      sublabel: 'draft',
    },
    {
      type: 'policies',
      id: 'pol_github_writes',
      label: 'GitHub · Write Gating',
      sublabel: 'active',
    },
    {
      type: 'policies',
      id: 'pol_finops_egress',
      label: 'FinOps · Token Egress Cap',
      sublabel: 'shadow',
    },
    { type: 'policies', id: 'finance-strict', label: 'finance-strict', sublabel: 'active' },
  ],
  tools: [
    { type: 'tools', id: 'create_refund', label: 'create_refund', sublabel: 'Stripe MCP' },
    { type: 'tools', id: 'create_payout', label: 'create_payout', sublabel: 'Stripe MCP' },
    { type: 'tools', id: 'get_charge', label: 'get_charge', sublabel: 'Stripe MCP' },
    { type: 'tools', id: 'merge_pr', label: 'merge_pr', sublabel: 'GitHub MCP' },
    { type: 'tools', id: 'create_pr', label: 'create_pr', sublabel: 'GitHub MCP' },
    { type: 'tools', id: 'delete_branch', label: 'delete_branch', sublabel: 'GitHub MCP' },
    { type: 'tools', id: 'send_dm', label: 'send_dm', sublabel: 'Slack MCP' },
    { type: 'tools', id: 'post_message', label: 'post_message', sublabel: 'Slack MCP' },
    { type: 'tools', id: 'read_page', label: 'read_page', sublabel: 'Notion MCP' },
    { type: 'tools', id: 'update_page', label: 'update_page', sublabel: 'Notion MCP' },
    { type: 'tools', id: 'chat_completion', label: 'chat_completion', sublabel: 'OpenAI MCP' },
    { type: 'tools', id: 'transition_issue', label: 'transition_issue', sublabel: 'Jira MCP' },
  ],
};

export const RESOURCE_TYPE_LABEL: Record<ScopeResourceType, string> = {
  mcps: 'MCP servers',
  agents: 'Agents',
  policies: 'Policies',
  tools: 'Tools',
};

/* ─────────────────────────────── Permission state meta ─────────────────── */

export const PERMISSION_STATE_META: Record<
  PermissionState,
  { label: string; fg: string; bg: string }
> = {
  full: { label: 'Full', fg: LEVEL_META.full.fgClass, bg: LEVEL_META.full.bgClass },
  view: { label: 'View', fg: LEVEL_META.view.fgClass, bg: LEVEL_META.view.bgClass },
  none: { label: 'None', fg: LEVEL_META.none.fgClass, bg: LEVEL_META.none.bgClass },
};

/* ─────────────────────────────── Member view constants ─────────────────── */

export const EMPTY_FILTERS: MemberFilters = {
  status: [],
  role: [],
  team: [],
  seen: [],
  scopedOnly: false,
};

/** Filter-chip labels for the last-sign-in buckets. */
export const LAST_SEEN_LABEL: Record<Exclude<LastSignInBucket, null>, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  never: 'Never signed in',
};
